import {
  json,
  Link,
  MetaFunction,
  Outlet,
  redirect,
  useLoaderData,
  useLocation,
} from '@remix-run/react';
import { Routes } from '~/constants/routes';
import { withAugmentedContext } from '~/services/auth/Guards';
import { Navbar } from '~/components/navigation/navbar';
import { Button } from '~/components/ui/button';
import { BookOpenTextIcon, X } from 'lucide-react';
import { cn } from '~/components/ui/utils';
import { DocPage } from '~/components/docs-page';
import { UserSessionManager } from '~/services/auth/UserSessionManager';
import { useState, Fragment } from 'react';
import { Transition } from '@headlessui/react';

const sidebarItems = [
  {
    title: 'Introduction to Brokkr',
    items: [
      {
        title: 'Brokkr Overview',
        href: Routes.DocsBrokkrOverview,
      },
      {
        title: 'Brokkr Components',
        href: Routes.DocsBrokkrComponents,
      },
      {
        title: 'Brokkr Terms of Service',
        href: Routes.DocsBrokkrTermsOfService,
      },
    ],
  },
  {
    title: 'Brokkr API',
    items: [
      {
        title: 'Overview',
        href: Routes.DocsBrokkrApiOverview,
      },
      {
        title: 'Authentication',
        href: Routes.DocsBrokkrApiAuthentication,
      },
      {
        title: 'DCIM',
        href: Routes.DocsBrokkrApiDcim,
      },
      {
        title: 'Deployments',
        href: Routes.DocsBrokkrApiDeployments,
      },
      {
        title: 'Inventory',
        href: Routes.DocsBrokkrApiInventory,
      },
      {
        title: 'Reservations',
        href: Routes.DocsBrokkrApiReservations,
      },
      {
        title: 'Webhooks',
        href: Routes.DocsBrokkrApiWebhooks,
      },
    ],
  },
  {
    title: 'Brokkr Bridge',
    items: [
      {
        title: 'Installation Overview',
        href: Routes.DocsBrokkrBridgeInstallationOverview,
      },
    ],
  },
  {
    title: 'Brokkr Security',
    items: [
      {
        title: 'Brokkr Security Documentation',
        href: Routes.DocsBrokkrSecurityBrokkrSecurityDocumentation,
      },
      {
        title: 'Brokkr Multi-Factor Authentication',
        href: Routes.DocsBrokkrSecurityMfa,
      },
    ],
  },
  {
    title: 'Brokkr FAQ',
    items: [
      {
        title: 'SSH Connectivity on VPN',
        href: Routes.DocsBrokkrFaqSshStopsOnVpn,
      },
      {
        title: 'SSH Keys with Brokkr',
        href: Routes.DocsBrokkrFaqSshKeysWithBrokkr,
      },
      {
        title: 'Troubleshooting NVIDIA SMI',
        href: Routes.DocsBrokkrFaqNvidiaSmiNotWorking,
      },
      {
        title: 'NVIDIA Driver and CUDA Toolkit',
        href: Routes.DocsBrokkrFaqNvidiaDriverAndCudaToolkit,
      },
    ],
  },
];

export const loader = withAugmentedContext(async ({ context }) => {
  return json({ user: context.user });
});

export default function DocsLayout() {
  const { user } = useLoaderData<typeof loader>();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <>
      <Navbar user={user as UserSessionManager} />

      <div className="pt-24 pb-12 px-4 md:px-6">
        <div className="mb-8 md:mb-12">
          <div className="flex flex-row items-start justify-between gap-4 lg:hidden mb-4">
            <h1 className="text-3xl font-bold text-white">Docs</h1>
            <Button
              variant="outline"
              className="flex items-center gap-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              <BookOpenTextIcon className="h-4 w-4" />
              View All Docs
            </Button>
          </div>

          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 md:gap-8">
            <div className="flex-1">
              <h1 className="hidden lg:block text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-3 md:mb-4">
                Docs
              </h1>
              <p className="text-base md:text-lg text-purple-light max-w-2xl">
                From renting GPUs to managing datacenters, the Brokkr platform
                has a wide variety of useful features and integrations. Get an
                overview of how to use them and how to get started.
              </p>
            </div>

            <div className="hidden lg:flex items-start">
              <Button asChild>
                <Link
                  className="flex items-center gap-2"
                  to="https://api.brokkr.hydrahost.com/api/redoc"
                  prefetch="intent"
                >
                  <BookOpenTextIcon className="w-4 h-4" />
                  View API Reference
                </Link>
              </Button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8 lg:gap-16 pt-4 md:pt-6">
          <div className="hidden lg:block lg:col-span-1 border-r border-border">
            <nav className="space-y-6 pr-8 overflow-y-auto max-h-[calc(100vh-12rem)] sticky top-28">
              {sidebarItems.map((section) => (
                <div key={section.title}>
                  <h3 className="text-sm font-bold text-white">
                    {section.title}
                  </h3>

                  <div className="py-2">
                    {section.items.map((item) => {
                      const isActive = location.pathname === item.href;

                      return (
                        <div
                          key={item.title}
                          className={cn(
                            'border-l-2 border-border pl-4 py-2',
                            isActive && 'border-primary',
                          )}
                        >
                          <Link
                            to={item.href}
                            prefetch="intent"
                            viewTransition
                            className={cn(
                              'block text-sm text-muted-foreground hover:text-white',
                              isActive && 'text-primary',
                            )}
                          >
                            {item.title}
                          </Link>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </nav>
          </div>

          <div className="col-span-1 lg:col-span-2">
            <Outlet />
          </div>

          <div className="hidden md:block lg:col-span-1">
            <DocPage.TableOfContents />
          </div>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      <Transition.Root show={mobileMenuOpen} as={Fragment}>
        <div className="fixed inset-0 z-50 lg:hidden">
          <Transition.Child
            as={Fragment}
            enter="ease-in-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in-out duration-300"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div
              className="fixed inset-0 bg-background/80 backdrop-blur-sm"
              onClick={() => setMobileMenuOpen(false)}
            />
          </Transition.Child>

          <Transition.Child
            as={Fragment}
            enter="transform transition ease-in-out duration-300"
            enterFrom="translate-x-full"
            enterTo="translate-x-0"
            leave="transform transition ease-in-out duration-300"
            leaveFrom="translate-x-0"
            leaveTo="translate-x-full"
          >
            <div className="fixed right-0 top-0 h-full w-full max-w-xs bg-background border-l border-border shadow-xl">
              <div className="flex items-center justify-between p-4 border-b border-border">
                <h2 className="text-lg font-semibold text-white">Docs Menu</h2>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <X className="h-5 w-5" />
                  <span className="sr-only">Close menu</span>
                </Button>
              </div>

              <nav className="p-4 space-y-6 overflow-y-auto max-h-[calc(100vh-5rem)]">
                {sidebarItems.map((section) => (
                  <div key={section.title}>
                    <h3 className="text-sm font-bold text-white mb-3">
                      {section.title}
                    </h3>

                    <div className="space-y-1">
                      {section.items.map((item) => {
                        const isActive = location.pathname === item.href;

                        return (
                          <Link
                            key={item.title}
                            to={item.href}
                            prefetch="intent"
                            viewTransition
                            onClick={() => setMobileMenuOpen(false)}
                            className={cn(
                              'block px-3 py-2 rounded-md text-sm transition-colors',
                              isActive
                                ? 'bg-primary/10 text-primary font-medium'
                                : 'text-muted-foreground hover:text-white hover:bg-muted',
                            )}
                          >
                            {item.title}
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                ))}

                <div className="pt-6 border-t border-border">
                  <Button asChild className="w-full">
                    <Link
                      className="flex items-center justify-center gap-2"
                      to="https://api.brokkr.hydrahost.com/api/redoc"
                      prefetch="intent"
                    >
                      <BookOpenTextIcon className="w-4 h-4" />
                      View API Reference
                    </Link>
                  </Button>
                </div>
              </nav>
            </div>
          </Transition.Child>
        </div>
      </Transition.Root>
    </>
  );
}

export const meta: MetaFunction = () => {
  return [
    { title: 'Brokkr Docs' },
    { name: 'description', content: 'Brokkr Docs' },
  ];
};
